// API Types matching the FastAPI backend models

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

export interface PlayerGameStats {
  csr?: number;
  skill_level?: number;
  skill_class?: string;
  matches_played?: number;
  wins?: number;
  losses?: number;
  win_percentage?: number;
  rank?: number;
  local_rank?: string;
  national_rank?: string;
  points_for?: number;
  points_against?: number;
}

export interface DivisionPlayer {
  player_id: string;
  name: string;
  team_name?: string;
  games: {
    [gameType: string]: PlayerGameStats;
  };
}

export interface DivisionRankingsResponse {
  division_id: string;
  players: DivisionPlayer[];
  sort_criteria: string;
  grouped_by_team: boolean;
  total_players: number;
  last_updated?: string;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  version: string;
}

export interface RefreshRequest {
  player_ids?: string[];
  force?: boolean;
}

export interface RefreshResponse {
  message: string;
  players_updated: number;
  division_id: string;
  timestamp: string;
  status?: string;
  match_history_status?: string;
}

export interface ApiError {
  detail: string;
  error_code?: string;
  timestamp?: string;
}

// Sort options matching the backend enum
export enum SortOption {
  EIGHTBALL_CSR = '8ball-csr',
  NINEBALL_CSR = '9ball-csr',
  TENBALL_CSR = '10ball-csr',
  EIGHTBALL_SKILL = '8ball-skill',
  NINEBALL_SKILL = '9ball-skill',
  TENBALL_SKILL = '10ball-skill',
  EIGHTBALL_CLASS = '8ball-class',
  NINEBALL_CLASS = '9ball-class',
  TENBALL_CLASS = '10ball-class',
  EIGHTBALL_RANK = '8ball-rank',
  NINEBALL_RANK = '9ball-rank',
  TENBALL_RANK = '10ball-rank',
  EIGHTBALL_NATIONAL = '8ball-national',
  NINEBALL_NATIONAL = '9ball-national',
  TENBALL_NATIONAL = '10ball-national',
  NAME = 'name',
  TEAM = 'team',
  MATCHES_8BALL = '8ball-matches',
  MATCHES_9BALL = '9ball-matches',
  MATCHES_10BALL = '10ball-matches',
  WIN_PCT_8BALL = '8ball-winpct',
  WIN_PCT_9BALL = '9ball-winpct',
  WIN_PCT_10BALL = '10ball-winpct'
}

// UI-specific types
export interface TableFilters {
  search: string;
  gameType: '8ball' | '9ball' | 'all';
  sortBy: SortOption;
  sortDirection: 'asc' | 'desc';
  groupByTeam: boolean;
}

export interface LoadingState {
  isLoading: boolean;
  isRefreshing: boolean;
  refreshingPlayers: string[];
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

// Historical Data Types - Match history CSR timeline
export interface CSRTimelinePoint {
  date: string;
  csr: number;
  result?: string | null; // 'W', 'L', or null for missed weeks
  played?: boolean;
  point_type?: 'win' | 'loss' | 'missed';
  // Match detail fields — only present for played points
  opponent_name?: string | null;
  player_games_won?: number | null;
  opponent_games_won?: number | null;
  player_race?: number | null;
  opponent_race?: number | null;
  venue?: string | null;
  division_name?: string | null;
  division_id?: string | null;
  is_makeup_match?: boolean | null;
  csr_before?: number | null;
  // Multi-match same-day clustering
  same_day_index?: number | null;
  same_day_count?: number | null;
}

export interface CSRTimelineResponse {
  player_id: string;
  game_type: string;
  total_points: number;
  timeline: CSRTimelinePoint[];
}

// Season (merged division pair) types
export interface SeasonRankingsResponse {
  season_id: string;
  division_ids: string[];
  players: DivisionPlayer[];
  total_players: number;
  metadata: {
    game_types: string[];
    divisions_loaded: string[];
  };
  sort_criteria: string;
  grouped_by_team: boolean;
}

export interface SeasonInfo {
  season_id: string;
  division_ids: string[];
  divisions: DivisionInfo[];
  player_count: number;
  match_count: number;
  first_match_date: string | null;
  last_match_date: string | null;
}

// Division/Season types
export interface DivisionInfo {
  division_id: string;
  name: string | null;
  first_match_date: string | null;
  last_match_date: string | null;
  player_count: number;
  match_count: number;
  has_cached_rankings?: boolean;
}

export interface DivisionsListResponse {
  divisions: DivisionInfo[];
}

// Division period stats — CSR at the end of a specific division/season
export interface PeriodGameStat {
  csr: number;
  as_of: string;
}

export interface DivisionPeriodStatsResponse {
  division_id: string;
  player_stats: Record<string, Record<string, PeriodGameStat>>;
}

// Matchup optimizer types
export interface ScheduleMatchup {
  week: number;
  date: string;
  home_team: string;
  away_team: string;
  home_team_id: string | null;
  away_team_id: string | null;
  score_home: number | null;
  score_away: number | null;
  is_bye: boolean;
  is_played: boolean;
  comp_sheet_url: string | null;
}

export interface ScheduleResponse {
  division_id: string;
  league_name: string;
  matchups: ScheduleMatchup[];
  current_week: number | null;
}

export interface ScorecardPlayer {
  player_id: string;
  name: string;
  csr: number | null;
}

export interface ScorecardMatch {
  home_player: ScorecardPlayer;
  away_player: ScorecardPlayer;
  home_race: number | null;
  away_race: number | null;
  home_games_won: number | null;
  away_games_won: number | null;
  home_score: number | null;
  away_score: number | null;
  winner: 'home' | 'away' | null;
  game_type: string;
}

export interface SessionScorecardResponse {
  division_id: string;
  week: number;
  date: string;
  home_team: string;
  away_team: string;
  score_home: number | null;
  score_away: number | null;
  matches: ScorecardMatch[];
  unmatched: Array<{ player_id: string; opponent_name: string | null; game_type: string | null }>;
}

export interface CompPlayer {
  name: string;
  player_id: string;
  csr: number;
  session_matches: number;
  wins: number;
  losses: number;
  win_pct: number;
  is_captain: boolean;
}

export interface RaceCell {
  home_race: number;
  away_race: number;
  h2h_wins: number | null;
  h2h_losses: number | null;
}

export interface CompSheetResponse {
  division_id: string;
  game_type: string;
  home_team_name: string;
  away_team_name: string;
  home_team_id: string;
  away_team_id: string;
  home_players: CompPlayer[];
  away_players: CompPlayer[];
  race_grid: Record<string, Record<string, RaceCell>>;
}

export interface WinProbModel {
  home_player_id: string;
  away_player_id: string;
  home_player_name: string;
  away_player_name: string;
  home_win_prob: number;
  away_win_prob: number;
  home_race: number;
  away_race: number;
  expected_home_score: number;
  expected_away_score: number;
  h2h_record: string | null;
  reasoning: string;
}

export interface OptimalPick {
  player_id: string;
  player_name: string;
  score: number;
  win_prob: number;
  reasoning: string;
}

export interface MatchResultInput {
  home_player_id: string;
  away_player_id: string;
  winner: 'home' | 'away' | null;
}

export interface AnalyzeRequest {
  division_id: string;
  home_team_id: string;
  away_team_id: string;
  user_team: 'home' | 'away';
  selected_own: string[];
  selected_opp: string[];
  matches_decided: MatchResultInput[];
  opponent_putup_id?: string;
  who_puts_up?: 'user' | 'opponent';
  game?: string;
  week_number?: number;
  week_day?: string;
}

export interface AnalysisResponse {
  user_team: string;
  next_action: string;
  optimal_putup: OptimalPick | null;
  optimal_counters: OptimalPick[];
  opponent_putup_id: string | null;
  probability_matrix: WinProbModel[];
  best_strategy_score: number;
}

export interface RosterSuggestion {
  players: string[];
  player_names: string[];
  score: number;
}

export interface OptimalRosterResponse {
  user_team: string;
  suggestions: RosterSuggestion[];
}

// Head-to-Head types
export interface HeadToHeadEncounter {
  date: string;
  game_type: string;
  winner_id: string | null;
  player_a_csr: number | null;
  player_b_csr: number | null;
  player_a_race: number | null;
  player_b_race: number | null;
  player_a_games_won: number | null;
  player_b_games_won: number | null;
  player_a_score: number | null;
  player_b_score: number | null;
  venue: string | null;
  division_id: string | null;
  division_name: string | null;
  is_makeup_match: boolean;
}

export interface HeadToHeadSummary {
  total_matches: number;
  player_a_wins: number;
  player_b_wins: number;
  player_a_current_streak: number;
  player_b_current_streak: number;
  player_a_avg_csr: number | null;
  player_b_avg_csr: number | null;
}

export interface HeadToHeadResponse {
  player_a_id: string;
  player_a_name: string;
  player_b_id: string;
  player_b_name: string;
  summary: HeadToHeadSummary;
  encounters: HeadToHeadEncounter[];
}
